/**
 * The clients somebody is on, from their own page.
 *
 * The same assignments the client page edits, turned the other way round: a
 * partner putting a new hire on three clients does it here, once, rather than
 * opening three client pages. Being on a client is what lets somebody on the
 * team see it at all, so this card is the answer to "why can't Zaid see Wood
 * & Metal?".
 */

import { X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { AssignmentRole } from '../../api';
import { BentoCard } from '../../components/BentoCard';
import { Button } from '../../components/Button';
import { Select } from '../../components/Select';
import { useAppData } from '../../context/AppDataContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { apiErrorMessage } from '../../lib/api';
import { cn } from '../../lib/cn';
import { PICK, ROLES, ROLE_LABEL } from '../clients/ClientPeopleAndPlan';

export function PersonClients({
  personId,
  name,
  openByClient
}: {
  personId: string;
  name: string;
  /** Open work of theirs per client id, to show beside each client. */
  openByClient: Map<string, number>;
}) {
  const { assignments, assignPerson, unassignPerson, clients, currentClients } = useAppData();
  const { hasRole } = useAuth();
  const { showToast } = useToast();
  // Staffing a client and setting its plan is a manager's job as much as an Admin's.
  const isPartner = hasRole('PARTNER', 'MANAGER');
  const [adding, setAdding] = useState('');
  const [addRole, setAddRole] = useState<AssignmentRole>('member');
  const [busy, setBusy] = useState(false);

  const mine = assignments
    .filter((a) => a.person_id === personId)
    .map((a) => ({ a, client: clients.find((c) => c.id === a.client_id) }))
    .sort(
      (x, y) =>
        ROLES.indexOf(x.a.role) - ROLES.indexOf(y.a.role) ||
        (x.client?.name ?? x.a.client_id).localeCompare(y.client?.name ?? y.a.client_id)
    );
  const available = currentClients.filter((c) => !mine.some((m) => m.a.client_id === c.id));
  // Work they have on a client they are not on: worth seeing, because it is
  // usually a client somebody forgot to put them on.
  const unlisted = [...openByClient.entries()]
    .filter(([id, n]) => n > 0 && !mine.some((m) => m.a.client_id === id))
    .map(([id, n]) => ({ client: clients.find((c) => c.id === id), n }))
    .filter((r) => r.client !== undefined);

  const run = async (work: () => Promise<void>, said: string) => {
    setBusy(true);
    try {
      await work();
      showToast(said);
    } catch (err) {
      showToast(apiErrorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  const first = name.split(' ')[0] ?? name;

  return (
    <BentoCard title="Clients" description="Who they work for, and how">
      {mine.length === 0 ? (
        <p className="text-sm text-shell-text">
          Not on any client yet. Until they are, they see no client work at all.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {mine.map(({ a, client }) => {
            const clientName = client?.name ?? a.client_id;
            const open = openByClient.get(a.client_id) ?? 0;
            return (
              <li key={a.client_id} className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: client?.color ?? 'var(--color-shell-border)' }}
                />
                <div className="min-w-[8rem] flex-1">
                  <Link
                    to={`/clients/${encodeURIComponent(a.client_id)}`}
                    className="block truncate text-sm font-medium text-shell-text-active hover:underline"
                  >
                    {clientName}
                  </Link>
                  <p className="text-xs text-shell-text tabular-nums">
                    {open === 0 ? 'Nothing open' : `${open} open`}
                  </p>
                </div>
                {isPartner ? (
                  <>
                    <Select
                      aria-label={`How ${name} is on ${clientName}`}
                      value={a.role}
                      disabled={busy}
                      onChange={(e) => {
                        const role = e.target.value as AssignmentRole;
                        void run(
                          () => assignPerson(a.client_id, personId, role),
                          `${first} is now ${ROLE_LABEL[role].toLowerCase()} on ${clientName}.`
                        );
                      }}
                      className={cn(PICK, 'w-36 shrink-0')}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABEL[r]}
                        </option>
                      ))}
                    </Select>
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={`Take ${name} off ${clientName}`}
                      title={`Take ${name} off ${clientName}`}
                      onClick={() =>
                        void run(() => unassignPerson(a.client_id, personId), `${first} is off ${clientName}.`)
                      }
                      className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-shell-text hover:bg-shell-border/40 hover:text-danger-500"
                    >
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <span className="shrink-0 rounded-full bg-shell-border/40 px-2.5 py-1 text-xs text-shell-text">
                    {ROLE_LABEL[a.role]}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {unlisted.length > 0 && (
        <p className="mt-4 text-xs text-shell-text">
          Also has open work on{' '}
          {unlisted.map((u, i) => (
            <span key={u.client!.id}>
              {i > 0 && ', '}
              <span className="font-medium text-shell-text-active">{u.client!.name}</span> ({u.n})
            </span>
          ))}
          , without being on {unlisted.length === 1 ? 'it' : 'them'}.
        </p>
      )}

      {isPartner && available.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-shell-border pt-4">
          <Select
            aria-label="Client to add"
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            className={cn(PICK, 'min-w-[10rem] flex-1')}
          >
            <option value="">Put them on a client…</option>
            {available.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="As"
            value={addRole}
            onChange={(e) => setAddRole(e.target.value as AssignmentRole)}
            className={cn(PICK, 'w-36')}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            disabled={adding === '' || busy}
            onClick={() => {
              const clientName = available.find((c) => c.id === adding)?.name ?? 'the client';
              void run(async () => {
                await assignPerson(adding, personId, addRole);
                setAdding('');
              }, `${first} is on ${clientName}.`);
            }}
          >
            Add
          </Button>
        </div>
      )}
    </BentoCard>
  );
}
